/**
 * Canonical property_rooms + checklist_room_links helpers.
 * Used by owner Checklist tab and property onboarding so checklist rooms stay in sync with the Rooms tab.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Room } from "@/types/checklist";
import { matchRoom, normalizeRoomName } from "@/lib/roomUtils";

/** Matches `property_rooms` select used for resolution (avoids importing hooks from this module). */
export interface PropertyRoomResolutionRow {
  id: string;
  property_id: string;
  name: string;
  name_normalized: string;
  room_instructions: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
}

/** Replace all checklist_room_links for a checklist (same behavior as useSetChecklistRoomLinks). */
export async function replaceChecklistRoomLinks(
  checklistId: string,
  propertyRoomIds: string[],
  roomNamesOriginal?: (string | null)[]
): Promise<void> {
  await supabase.from("checklist_room_links").delete().eq("checklist_id", checklistId);
  if (propertyRoomIds.length === 0) return;
  // checklist_room_links has UNIQUE(checklist_id, property_room_id); cloned checklists
  // can list the same canonical room twice under different names.
  const seen = new Set<string>();
  const dedupedIds: string[] = [];
  const dedupedOriginals: (string | null)[] = [];
  for (let i = 0; i < propertyRoomIds.length; i++) {
    const id = propertyRoomIds[i];
    if (seen.has(id)) continue;
    seen.add(id);
    dedupedIds.push(id);
    dedupedOriginals.push(roomNamesOriginal?.[i] ?? null);
  }
  const rows = dedupedIds.map((property_room_id, i) => ({
    checklist_id: checklistId,
    property_room_id,
    room_name_original: dedupedOriginals[i] ?? null,
  }));
  const { error } = await supabase.from("checklist_room_links").insert(rows);
  if (error) throw error;
}

/**
 * For each checklist room: find or create property_rooms, attach property_room_id to the room JSON.
 * Mirrors owner Checklist tab behavior (exact name match only; no fuzzy auto-merge).
 */
export async function resolveRoomsToPropertyRoomsForProperty(
  propertyId: string,
  roomsToResolve: Room[],
  initialExisting: PropertyRoomResolutionRow[]
): Promise<{ rooms: Room[]; propertyRoomIds: string[]; roomNamesOriginal: (string | null)[] }> {
  const resolved: Room[] = [];
  const propertyRoomIds: string[] = [];
  const roomNamesOriginal: (string | null)[] = [];
  let currentList = [...initialExisting];

  for (const room of roomsToResolve) {
    const name = room.name.trim();
    if (!name) continue;
    const match = matchRoom(name, currentList.map((r) => ({ id: r.id, name: r.name })));
    let propertyRoomId: string;
    let canonicalName: string;
    if (match.exact) {
      propertyRoomId = match.exact.id;
      canonicalName = match.exact.name;
    } else {
      const name_normalized = normalizeRoomName(name);
      const { data, error } = await supabase
        .from("property_rooms")
        .insert({
          property_id: propertyId,
          name: name,
          name_normalized,
          room_instructions: null,
          notes: null,
        })
        .select("id, property_id, name, name_normalized, room_instructions, notes, archived_at, created_at")
        .single();
      if (error) {
        if (error.code === "23505") throw new Error("A room with this name already exists");
        throw error;
      }
      const created = data as PropertyRoomResolutionRow;
      propertyRoomId = created.id;
      canonicalName = created.name;
      currentList = [...currentList, created];
    }
    propertyRoomIds.push(propertyRoomId);
    roomNamesOriginal.push(name);
    resolved.push({
      ...room,
      name: canonicalName,
      property_room_id: propertyRoomId,
      inventory_room_id: room.inventory_room_id ?? undefined,
    });
  }
  return { rooms: resolved, propertyRoomIds, roomNamesOriginal };
}
